import { useCallback } from 'react'
import { getApiUrlObject } from './api'
import 'isomorphic-fetch'

const useRequester = ({ authService, alertHandler, logoutCallback }) => {
  const logout = useCallback(() => {
    authService.logout()
    logoutCallback()
  }, [authService, logoutCallback])

  const { displayAlert } = alertHandler

  const request = useCallback(
    async (
      path,
      { fetchOptions, params, displayAlerts = true, allowLogouts = true } = {}
    ) => {
      if (!authService.getToken()) {
        logout()
        return { error: new Error('NO_TOKEN') }
      }

      const alert = alertData => displayAlerts && displayAlert(alertData)
      const url = getApiUrlObject(
        path + (params ? `?${new URLSearchParams(params).toString()}` : '')
      )
      try {
        // Pass href string as 'jest-fetch-mock' doesn't support URL objects yet
        // This can be removed when https://github.com/jefflau/jest-fetch-mock/pull/193 is released
        const response = await fetch(url.href, {
          ...fetchOptions,
          headers: {
            ...authService.getAuthorizationHeader(),
            ...fetchOptions?.headers, // Provided headers
          },
        })

        if (response.status === 401 || response.status === 403) {
          logout()
          alert({
            type: 'warning',
            message: 'Your login session has expired. Please login again.',
          })
          return { error: new Error('UNAUTHORIZED') }
        }

        if (response.status === 500) {
          alert({
            type: 'danger',
            message:
              'The server was unable to process your request. If the problem persists, please contact the IT Helpdesk and let us know about your issue.',
          })
          return { error: new Error('SERVER_ERROR') }
        }

        if (response.ok) {
          try {
            return { data: await response.json() }
          } catch (e) {
            // Handle invalid (non-JSON) response data being returned from server
            return { data: null }
          }
        }

        // Display error for any other types of invalid responses
        return { error: new Error(`UNHANDLED_RESPONSE-${response.status}`) }
      } catch (e) {
        alert({
          type: 'danger',
          message:
            'Error accessing server. Check that you are connected to the Internet. If the problem persists, please contact the IT Helpdesk and let us know about your issue.',
        })
        return { error: e }
      }
    },
    [authService, displayAlert, logout]
  )

  return { request, logout }
}

export default useRequester
